import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface AdminPropertiesFilter {
  selectedOrganizationsFilter: string[];
  searchAdminPropertiesTerm: string;
  selectedWarrantyStatusFilter: string[];
}

interface AdminPropertiesFiltersState {
  // State
  filters: AdminPropertiesFilter;

  // Actions
  setFilters: (newFilters: Partial<AdminPropertiesFilter>) => void;
  clearFilters: () => void;
  setSearchTerm: (term: string) => void;
  setOrganizationFilters: (organizations: string[]) => void;
  setWarrantyStatusFilters: (statuses: string[]) => void;
}

const initialFilters: AdminPropertiesFilter = {
  selectedOrganizationsFilter: [],
  selectedWarrantyStatusFilter: [],
  searchAdminPropertiesTerm: "",
};

// DevTools configuration options
const devToolsOptions = {
  // Name of the store in Redux DevTools (helps when you have multiple stores)
  name: "AdminPropertiesFilters",

  // Enable/disable DevTools based on environment (optional)
  // You could use process.env.NODE_ENV !== 'production' here
  enabled: true,

  // Custom anonymous action type (optional)
  anonymousActionType: "UNKNOWN_ACTION",

  // Serialize options (optional - used for handling complex objects)
  serialize: {
    // Options for handling circular references
    options: true,
  },
};

// State persists across React Router navigations (module singleton) and
// resets naturally on page refresh — no localStorage needed.
export const useAdminPropertiesFiltersStore =
  create<AdminPropertiesFiltersState>()(
    // DevTools should be the outermost middleware
    devtools(
      (set) => ({
        // Initial state
        filters: initialFilters,

        // Actions
        setFilters: (newFilters) =>
          // The third parameter to set allows us to name the action for DevTools
          set(
            (state) => ({
              filters: { ...state.filters, ...newFilters },
            }),
            false,
            {
              type: "admin-properties-filters/setFilters",
              payload: newFilters,
            }
          ),

        clearFilters: () =>
          set(
            { filters: initialFilters },
            false,
            "admin-properties-filters/clearFilters"
          ),

        setSearchTerm: (searchAdminPropertiesTerm) =>
          set(
            (state) => ({
              filters: { ...state.filters, searchAdminPropertiesTerm },
            }),
            false,
            {
              type: "admin-properties-filters/setSearchTerm",
              payload: searchAdminPropertiesTerm,
            }
          ),

        setWarrantyStatusFilters: (selectedWarrantyStatusFilter) =>
          set(
            (state) => ({
              filters: { ...state.filters, selectedWarrantyStatusFilter },
            }),
            false,
            {
              type: "admin-properties-filters/setWarrantyStatusFilters",
              payload: selectedWarrantyStatusFilter,
            }
          ),

        setOrganizationFilters: (selectedOrganizationsFilter) =>
          set(
            (state) => ({
              filters: { ...state.filters, selectedOrganizationsFilter },
            }),
            false,
            {
              type: "admin-properties-filters/setOrganizationFilters",
              payload: selectedOrganizationsFilter,
            }
          ),
      }),
      // Pass DevTools options as the second parameter to devtools
      devToolsOptions
    )
  );
